/**
 * Main Application Layout
 *
 * Provides the Shell layout for all main app routes.
 * Features:
 * - Main app navigation items
 * - User-focused interface
 * - Optional admin app switcher (if user has permission)
 * - No organization selection (main app is not org-scoped)
 */

import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	Briefcase02,
	Building07,
	Cube01,
	Home02,
	LayersThree01,
	Map01,
	Monitor01,
	Ticket01,
} from "@untitledui/icons";
import { useEffect, useMemo } from "react";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { AppToaster } from "@/components/application/notifications/toaster";
import { Shell } from "@/components/Organisms/Shell/Shell";
import { waitForConfig } from "@/config";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { useOrgAuditLiveUpdates } from "@/features/live-updates/use-org-audit-live-updates";
import { authQueryOptions } from "@/services/auth";
import { organizationQueryOptions } from "@/services/organization";
import { useAppActions, useCurrentOrganization } from "@/stores/app-hooks";
import { authStore } from "@/stores/auth-store";
import {
	isWebOrgListReady,
	isWebUrlOrgIdInvalid,
	resolveConcreteOrgId,
	resolveWebAvailableOrganizations,
	selectActiveOrganizationUserMemberships,
	selectOrgAdminAccessOrganizationId,
	shouldRedirectInvalidWebOrg,
} from "@/utils/web-org-redirect";

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute("/_authenticated/(web-app)/_web")({
	component: MainLayout,
	beforeLoad: async ({ context }) => {
		await waitForConfig();
		try {
			await context.queryClient.ensureQueryData(authQueryOptions.userInfo());
		} catch {
			// API may be down (e.g. ECONNREFUSED in dev); avoid unhandled rejection / process exit
		}
	},
});

// ============================================================================
// LAYOUT COMPONENT
// ============================================================================

function MainLayout() {
	const auth = useAuth();
	const navigate = useNavigate();
	const params = useParams({ strict: false });
	const { t } = useAppTranslation();

	const userInfoQuery = useQuery(authQueryOptions.userInfo());

	const isPlatformAdminQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
		enabled: !!userInfoQuery.data,
	});

	const activeMemberships = useMemo(
		() =>
			selectActiveOrganizationUserMemberships(
				userInfoQuery.data?.organizationUserInfo ?? [],
			),
		[userInfoQuery.data?.organizationUserInfo],
	);
	// user-info already evaluates ViewOrganizationInfo per membership into
	// canViewOrganizationDetails. Do not re-check via /valid-actions here:
	// that endpoint uses the session organization and ignores entityId when
	// entityType is null, so a user on Org A would lose Switch App even when
	// Org B has canViewOrganizationDetails.
	const orgAdminAccessOrganizationId = useMemo(
		() =>
			selectOrgAdminAccessOrganizationId(
				userInfoQuery.data?.organizationUserInfo ?? [],
			),
		[userInfoQuery.data?.organizationUserInfo],
	);

	const isPlatformAdmin = isPlatformAdminQuery.data ?? false;
	const isOrgAdmin = !!orgAdminAccessOrganizationId;

	const allOrgs = useQuery({
		...organizationQueryOptions.all(),
		enabled: isPlatformAdmin,
	});

	const routeOrgId = (params as { orgId?: string }).orgId;
	const storedOrganization = useCurrentOrganization();

	const membershipOrganizations = useMemo(
		() =>
			activeMemberships
				.map((info) => info.organization)
				.filter((org): org is NonNullable<typeof org> => org != null),
		[activeMemberships],
	);

	const availableOrganizations = useMemo(
		() =>
			resolveWebAvailableOrganizations({
				isPlatformAdmin,
				allOrgs: allOrgs.data,
				membershipOrganizations,
			}),
		[isPlatformAdmin, allOrgs.data, membershipOrganizations],
	);
	const resolvedOrgId = resolveConcreteOrgId({
		urlOrgId: routeOrgId,
		storeOrgId: storedOrganization?.id,
		activeOrganizationId: authStore.getState().activeOrganizationId,
		userInfoCurrentOrganizationId: userInfoQuery.data?.currentOrganizationId,
		firstAvailableOrgId: availableOrganizations[0]?.id,
		availableOrganizations,
	});
	const currentOrgId = routeOrgId ?? resolvedOrgId ?? "unknown";
	useOrgAuditLiveUpdates(currentOrgId);

	// Find the current organization based on URL
	const currentOrganization = availableOrganizations.find(
		(org) => org.id === currentOrgId,
	) ??
		activeMemberships.find((info) => info.organization?.id === currentOrgId)
			?.organization ??
		activeMemberships[0]?.organization ?? {
			id: "",
			createdBy: null,
			createdAt: null,
			createdAtOffset: null,
			updatedBy: null,
			updatedAt: null,
			updatedAtOffset: null,
			name: null,
			statusCode: 0,
			oAuthProviderId: null,
			oAuthProvider: null,
			status: { name: "Created" as const, description: "", code: 0 },
			mfaEnabled: null,
		};

	// Sync app store with the web app's context so switchToOrganization routes correctly
	const { setApp, setAvailableOrganizations, setOrganization } =
		useAppActions();

	useEffect(() => {
		setApp("main");
		setAvailableOrganizations(availableOrganizations);
		if (currentOrganization?.id && currentOrganization.id !== "unknown") {
			setOrganization(currentOrganization.id);
			authStore
				.getState()
				.setActiveOrganization(currentOrganization.id);
		}
	}, [
		availableOrganizations,
		currentOrganization?.id,
		setApp,
		setAvailableOrganizations,
		setOrganization,
	]);

	// Redirect when URL has no valid org (e.g. /web/home or /web//home) so there is always an orgId.
	// Wait until the org list is authoritative so platform admins do not bounce to the first
	// membership org while ViewAllOrganizations / allOrgs are still loading.
	const firstOrgId = availableOrganizations[0]?.id;
	const urlOrgIdInvalid = isWebUrlOrgIdInvalid(
		currentOrgId,
		availableOrganizations,
	);
	const orgListReady = isWebOrgListReady({
		userInfoSuccess: userInfoQuery.isSuccess,
		viewAllOrganizationsFetched: isPlatformAdminQuery.isFetched,
		isPlatformAdmin,
		allOrgsFetched: allOrgs.isFetched,
	});

	useEffect(() => {
		if (
			!shouldRedirectInvalidWebOrg({
				isOrgListReady: orgListReady,
				firstOrgId,
				urlOrgIdInvalid,
			})
		) {
			return;
		}
		navigate({ to: `/web/${firstOrgId}/home` });
	}, [orgListReady, firstOrgId, urlOrgIdInvalid, navigate]);

	// ============================================================================
	// NAVIGATION CONFIGURATION
	// ============================================================================

	const mainNavigationItems: NavItemType[] = [
		{
			label: t("main.nav.items.home.label"),
			href: `/web/${currentOrgId}/home`,
			icon: Home02,
		},
		{
			label: t("main.nav.items.jobs.label"),
			href: `/web/${currentOrgId}/jobs`,
			icon: Briefcase02,
		},
		{
			label: t("main.nav.items.tickets.label"),
			href: `/web/${currentOrgId}/tickets`,
			icon: Ticket01,
		},
		{
			label: t("main.nav.items.assets.label"),
			href: `/web/${currentOrgId}/assets`,
			icon: Cube01,
		},
		{
			label: t("main.nav.items.records.label"),
			href: `/web/${currentOrgId}/records`,
			icon: LayersThree01,
		},
		{
			label: t("main.nav.items.map.label"),
			href: `/web/${currentOrgId}/map`,
			icon: Map01,
		},
	];
	const { src: userAvatarSrc } = useUserAvatarSrc(
		auth.user?.id || "",
		auth.user?.hasAvatar,
	);

	return (
		<>
			<Shell
				variant="brand"
				accountMode={isPlatformAdmin || isOrgAdmin ? "application" : "standard"}
				logoSrc="/images/LogoFull@2x.png"
				// Navigation
				items={mainNavigationItems}
				homeUrl={`/web/${currentOrgId}/home`}
				// User Info
				name={
					auth.user?.displayName ||
					auth.user?.firstName ||
					t("main.nav.user.name")
				}
				email={auth.user?.email || ""}
				onSignOut={auth.logout}
				onUserProfile={() => {
					// Navigate with profileDrawer search param (defined in _authenticated route)
					const currentSearch = new URLSearchParams(window.location.search);
					currentSearch.set("profileDrawer", "open");
					navigate({
						to: ".",
						search: Object.fromEntries(currentSearch) as Record<string, string>,
					});
				}}
				// Brand LeftNav — active/icon classes derived from variant in LeftNav
				activeBackgroundClass="bg-gray-100"
				iconClassName="text-gray-600"
				headerText={t("main.nav.title")}
				// Organization Card
				showOrgNavCard={true}
				showOrgNavSelector={
					isPlatformAdmin ? true : availableOrganizations.length > 1
				}
				user1Name={
					currentOrganization?.name || t("admin.menu.selectOrganization")
				}
				userAvatarSrc={userAvatarSrc || undefined}
				avatar1Src={Building07}
				avatar1Initials={currentOrganization?.name?.charAt(0) || "ORG"}
				avatar1BackgroundColor="bg-[var(--Colors-Warning-600,#DC6803)]"
				// Footer account card — always shown; SWITCH APP only for application mode
				showAppNavCard={true}
				showAppNavSelector={isPlatformAdmin || isOrgAdmin}
				user2Name={t("main.nav.title")}
				avatar2Src={Monitor01}
				avatar2Initials="M"
				avatar2BackgroundColor="bg-[rgba(255,255,255,0.12)]"
				availableOrganizations={availableOrganizations}
				currentOrganization={currentOrganization}
				disabled={availableOrganizations.length === 1}
				mainAppNavCardOnClickHandler={() => {
					navigate({
						to: `/web/${currentOrgId}/home`,
					});
				}}
				adminAppNavCardOnClickHandler={() => {
					if (isPlatformAdmin) {
						if (resolvedOrgId) {
							setOrganization(resolvedOrgId);
							authStore.getState().setActiveOrganization(resolvedOrgId);
							navigate({
								to: `/org-admin/${resolvedOrgId}/home`,
							});
						} else {
							navigate({
								to: "/admin/home",
							});
						}
					} else if (isOrgAdmin && orgAdminAccessOrganizationId) {
						setOrganization(orgAdminAccessOrganizationId);
						authStore
							.getState()
							.setActiveOrganization(orgAdminAccessOrganizationId);
						navigate({
							to: `/org-admin/${orgAdminAccessOrganizationId}/home`,
						});
					}
				}}
				isPlatformAdmin={isPlatformAdmin}
				isOrgAdmin={isOrgAdmin}
			>
				<Outlet />
			</Shell>
			<AppToaster />
		</>
	);
}
